// Copyright (c) 2026 Michael F. Kamprath
//
// choice-plaque.js -- Quiz-mode choice plaque renderer + animators.
//
// Produces one PIXI Container per on-screen plaque (standard plaque
// background + numeral) and exposes the five animators called out in
// docs/QUIZ-MODE-LEGACY-SPEC §3 + §4:
//
//   animateChoicePlaqueEntrance    (0.30 s -- zoom-out + settle)
//   animateChoicePlaqueTouchIn     (0.275 s -- finger-down surge)
//   animateChoicePlaqueTouchOut    (0.40 s -- finger-dragged-out return)
//   animateChoicePlaqueCorrect     (0.95 s -- three-phase rotation envelope)
//   animateChoicePlaqueIncorrect   (0.40 s -- dim + image swap)
//
// The plaque container is structured so its local origin (0, 0) IS
// the plaque's visual center.  Rotating container.rotation rotates
// around the plaque's centre; scaling container.scale scales around
// the same point.  This is achieved naturally by anchoring the
// background sprite at (0.5, 0.5) at local (0, 0) and placing the
// numeral at an offset relative to (0, 0).  No container.pivot
// manipulation is required.
//
// Plaque containers DO NOT know which plaque is "correct".  That's a
// manager-level concern -- every plaque is built from standardImage
// and the incorrect animator is the only code path that swaps to
// incorrectImage.
//
// Cancellation: every animator accepts a cancel token
// ({ cancelled: boolean }).  Callers own the lifecycle -- when
// starting a new animation on a plaque mid-flight, trip the prior
// token's `cancelled` to true and pass a fresh token to the new
// animator.  The in-flight animator resolves early from its
// current state; the new animator proceeds from whatever scale /
// rotation / alpha the plaque was left at.  This mirrors the legacy
// iOS behaviour of animateWithDuration: replacing any prior
// animation on the same layer.

import * as PIXI from 'pixi.js';
import { animateOver } from './entrance.js';
import {
  computeGlyphCenteringYShift,
  fitFontSizeToWidth,
  formatNumeral,
  resolvePlaqueTexture,
} from './number-plaque.js';
import { getFontStackForScript } from '../numerals.js';

// --- Easing primitives ---------------------------------------------------
//
// Re-implemented here rather than imported from entrance.js so the latter
// doesn't have to widen its public API to expose internal helpers.  These
// are trivial.
const easeIn    = (t) => t * t;
const easeOut   = (t) => 1 - (1 - t) * (1 - t);
const easeInOut = (t) => -0.5 * (Math.cos(Math.PI * t) - 1);
const lerp      = (a, b, t) => a + (b - a) * t;
const toRad     = (deg) => (deg * Math.PI) / 180;

// ------------------------------------------------------------------------
// Scale derivation -- QUIZ-MODE-LEGACY-SPEC §3 + §11-Q2
// ------------------------------------------------------------------------
//
// Legacy plist ships displayScale / touchScale / correctScale /
// incorrectScale directly; displayMaxScale (1.1 x) and touchMaxScale
// (1.05 x) are derived at runtime by ThemeConfig.m:507 + :518.  Keep
// the overshoot constants in one place so a future tweak touches one
// line.

const DISPLAY_OVERSHOOT = 1.1;    // legacy ThemeConfig.m:507
const TOUCH_OVERSHOOT   = 1.05;   // legacy ThemeConfig.m:518

/**
 * Resolve the six scale values a plaque animates between, given the
 * theme's quizPlaque cfg block.  Missing keys fall back to `display`
 * so a minimal cfg { displayScale } still yields sane values.
 *
 * @param {object} cfg  Theme's quizPlaque block.
 * @returns {{
 *   display: number, displayMax: number,
 *   touch: number, touchMax: number,
 *   correct: number, incorrect: number,
 * }}
 */
export function quizPlaqueScales(cfg) {
  const display   = Number.isFinite(cfg?.displayScale)   ? cfg.displayScale   : 1;
  const touch     = Number.isFinite(cfg?.touchScale)     ? cfg.touchScale     : display;
  const correct   = Number.isFinite(cfg?.correctScale)   ? cfg.correctScale   : display;
  const incorrect = Number.isFinite(cfg?.incorrectScale) ? cfg.incorrectScale : display;
  return {
    display,
    displayMax: display * DISPLAY_OVERSHOOT,
    touch,
    touchMax:   touch   * TOUCH_OVERSHOOT,
    correct,
    incorrect,
  };
}

// ------------------------------------------------------------------------
// Helpers
// ------------------------------------------------------------------------

function numeralOffsetFor(cfg) {
  const imgW = cfg?.imageSize?.w ?? 0;
  const imgH = cfg?.imageSize?.h ?? 0;
  const fr   = cfg?.numberFrame;
  if (!imgW || !imgH || !fr) return { x: 0, y: 0 };
  const numCx = fr.x + fr.w / 2;
  const numCy = fr.y + fr.h / 2;
  return { x: numCx - imgW / 2, y: numCy - imgH / 2 };
}

/**
 * Default numeral font-size multiplier.  Legacy iOS kFontScaleFactor
 * (InternationalNumberDisplayLayer.m) -- gives 10% padding above and
 * below the visible glyph inside numberFrame.h.  Overridable via
 * cfg.fontSizeMultiplier (resolved by the caller -- typically the
 * plaque manager merges per-theme + global-config values into cfg
 * before calling createChoicePlaque, so by the time this runs the
 * cfg value is the final resolved number).
 */
const DEFAULT_FONT_SIZE_MULTIPLIER = 0.80;

function numeralFontSize(cfg) {
  const raw = cfg?.fontSizeMultiplier;
  const mult = (Number.isFinite(raw) && raw > 0) ? raw : DEFAULT_FONT_SIZE_MULTIPLIER;
  return (cfg?.numberFrame?.h ?? 0) * mult;
}

/**
 * Set a container's uniform scale.  Tolerates both PIXI's .scale.set
 * API and lightweight test mocks that store x/y directly on the scale
 * object.  A helper pays for itself because every animator calls it
 * every frame.
 */
function setScale(container, s) {
  if (container.scale && typeof container.scale.set === 'function') {
    container.scale.set(s, s);
  } else if (container.scale) {
    container.scale.x = s;
    container.scale.y = s;
  }
}

function getScale(container) {
  return container.scale?.x ?? 1;
}

// ------------------------------------------------------------------------
// Render
// ------------------------------------------------------------------------

/**
 * Build a choice plaque container.
 *
 * The returned container's local origin (0, 0) is the plaque's visual
 * centre.  Callers position it by setting container.x / container.y
 * in the parent stage's coordinate system.
 *
 * Exposes:
 *   container._plaqueSprite       background sprite
 *   container._plaqueText         numeral Text node
 *   container._plaqueConfig       the cfg block passed in
 *   container._plaqueValue        the numeric value rendered
 *   container._standardTexture    stashed so the incorrect animator
 *                                 can verify it started from standard
 *   container._incorrectTexture   stashed so the incorrect animator
 *                                 can swap without re-resolving
 *
 * @param {object} opts
 * @param {object} opts.cfg              Theme's quizPlaque block.
 * @param {number} opts.value            Numeral to render.
 * @param {Map|Object} [opts.textureMap] { path -> Texture } lookup.
 * @param {string} [opts.numeralScript]  Numeral script key from numerals.json.
 * @param {string} [opts.fontFamily]     Override the default script-aware font stack.
 * @returns {PIXI.Container}
 */
export function createChoicePlaque({
  cfg,
  value,
  textureMap = null,
  numeralScript = 'arabic',
  fontFamily = getFontStackForScript(numeralScript),
}) {
  if (!cfg) throw new Error('createChoicePlaque: cfg required');
  if (!cfg.imageSize) {
    throw new Error('createChoicePlaque: cfg.imageSize required');
  }

  const container = new PIXI.Container();
  container._plaqueConfig = cfg;
  container._plaqueValue = value;

  // Background sprite starts on standardImage.  The incorrect animator
  // is the only path that swaps the texture.
  const standardTex = resolvePlaqueTexture(cfg.standardImage, textureMap);
  const incorrectTex = cfg.incorrectImage
    ? resolvePlaqueTexture(cfg.incorrectImage, textureMap)
    : null;

  const sprite = new PIXI.Sprite(standardTex);
  if (sprite.anchor && typeof sprite.anchor.set === 'function') {
    sprite.anchor.set(0.5, 0.5);
  }
  sprite.width  = cfg.imageSize.w;
  sprite.height = cfg.imageSize.h;
  container.addChild(sprite);
  container._plaqueSprite = sprite;
  container._standardTexture = standardTex;
  container._incorrectTexture = incorrectTex;

  // Numeral
  const sample = formatNumeral(value, numeralScript);
  // Height-derived candidate; shrunk to fit width if needed (Japanese
  // hiragana like 'じゅうきゅう' overflows the arabic-sized frame).
  const candidateFontSize = numeralFontSize(cfg);
  const maxTextWidth = cfg?.numberFrame?.w ?? 0;
  const fontSize = fitFontSizeToWidth({
    sample,
    candidateFontSize,
    maxWidth: maxTextWidth,
    fontFamily,
    fontWeight: '700',
  });
  const text = new PIXI.Text({
    text: sample,
    style: {
      fontFamily,
      fontSize,
      fill: cfg.fontColor || '#000000',
      fontWeight: '700',
      align: 'center',
    },
  });
  if (text.anchor && typeof text.anchor.set === 'function') {
    text.anchor.set(0.5, 0.5);
  }
  const offset = numeralOffsetFor(cfg);
  const yShift = computeGlyphCenteringYShift({
    sample, fontSize, fontFamily, fontWeight: '700',
  });
  text.x = offset.x;
  text.y = offset.y + yShift;
  container.addChild(text);
  container._plaqueText = text;

  return container;
}

/**
 * Preload every theme's choice-plaque textures (standard + incorrect).
 * Returns a Map keyed by raw path, matching the convention of
 * number-plaque.preloadPlaqueTextures.
 *
 * @param {object} themes  Parsed themes.json root object.
 * @returns {Promise<Map<string, PIXI.Texture>>}
 */
export async function preloadChoicePlaqueTextures(themes) {
  const map = new Map();
  const { Assets } = PIXI;
  if (!Assets) return map;

  const paths = new Set();
  for (const theme of Object.values(themes || {})) {
    const quizCfg = theme?.quizPlaque;
    if (!quizCfg) continue;
    if (quizCfg.standardImage)  paths.add(quizCfg.standardImage);
    if (quizCfg.incorrectImage) paths.add(quizCfg.incorrectImage);
  }

  for (const p of paths) {
    const alias = '/' + p;
    const cached = Assets.get(alias);
    if (cached) { map.set(p, cached); continue; }
    try { Assets.add({ alias, src: alias }); }
    catch { /* already registered; non-fatal */ }
    try {
      const tex = await Assets.load(alias);
      if (tex) map.set(p, tex);
    } catch (err) {
      if (import.meta.env?.DEV) {
        // eslint-disable-next-line no-console
        console.warn(`[choice-plaque] failed to load ${alias}:`, err);
      }
    }
  }
  return map;
}

// ------------------------------------------------------------------------
// Animators
// ------------------------------------------------------------------------

/**
 * Plaque entrance -- spec §3.
 *
 *   t=0.00                        scale=0.10, alpha=0.30
 *   t=0.00 → phase1Ms             scale 0.10 → displayMax, alpha 0.30 → 1.0 (easeOut)
 *   t=phase1Ms → phase1+phase2    scale displayMax → display (easeIn)
 *
 * Default timings match the legacy spec (200 ms + 100 ms = 300 ms
 * total).  Both phases are configurable via opts so app-config.json
 * can slow the reveal down without a rebuild.
 *
 * The optional pop sound (POP_SEQUENCE indexed by entrance order) is
 * fired by the caller, NOT this animator -- sequencing + indexing is
 * manager state, not renderer state.
 *
 * @param {*} plaque
 * @param {{
 *   cfg: object,
 *   cancel?: { cancelled: boolean },
 *   phase1Ms?: number,
 *   phase2Ms?: number,
 * }} opts
 */
export async function animateChoicePlaqueEntrance(plaque, opts) {
  const { cfg, cancel, phase1Ms = 200, phase2Ms = 100 } = opts;
  const { display, displayMax } = quizPlaqueScales(cfg);

  plaque.visible = true;
  plaque.alpha = 0.30;
  plaque.rotation = 0;
  setScale(plaque, 0.10);

  // Phase 1: 0.10 → displayMax, alpha 0.30 → 1.0 (easeOut).
  await animateOver(phase1Ms, (t) => {
    const e = easeOut(t);
    setScale(plaque, lerp(0.10, displayMax, e));
    plaque.alpha = lerp(0.30, 1.0, e);
  }, cancel);
  if (cancel?.cancelled) return;

  // Phase 2: displayMax → display (easeIn).
  await animateOver(phase2Ms, (t) => {
    const e = easeIn(t);
    setScale(plaque, lerp(displayMax, display, e));
  }, cancel);
  if (cancel?.cancelled) return;

  setScale(plaque, display);
  plaque.alpha = 1.0;
}

/**
 * Plaque touch-in -- spec §4.
 *
 *   t=0.000 → 0.200 s  scale → touchMax (1.05 × touchScale)
 *   t=0.200 → 0.275 s  scale → touchScale
 *
 * Starts from whatever scale the plaque is already at -- handles
 * mid-entrance or mid-touchOut interruption where the prior cancel
 * token has been tripped by the caller.
 */
export async function animateChoicePlaqueTouchIn(plaque, opts) {
  const { cfg, cancel } = opts;
  const { touch, touchMax } = quizPlaqueScales(cfg);
  const fromScale = getScale(plaque);

  await animateOver(200, (t) => {
    setScale(plaque, lerp(fromScale, touchMax, easeOut(t)));
  }, cancel);
  if (cancel?.cancelled) return;

  await animateOver(75, (t) => {
    setScale(plaque, lerp(touchMax, touch, easeIn(t)));
  }, cancel);
  if (cancel?.cancelled) return;

  setScale(plaque, touch);
}

/**
 * Plaque touch-out -- spec §4.
 *
 *   0.000 → 0.400 s  scale → displayScale  (easeInOut)
 *
 * Fires when the player's finger drags out of the plaque bounds
 * without releasing.  Starts from whatever scale the plaque is at.
 */
export async function animateChoicePlaqueTouchOut(plaque, opts) {
  const { cfg, cancel } = opts;
  const { display } = quizPlaqueScales(cfg);
  const fromScale = getScale(plaque);

  await animateOver(400, (t) => {
    setScale(plaque, lerp(fromScale, display, easeInOut(t)));
  }, cancel);
  if (cancel?.cancelled) return;

  setScale(plaque, display);
}

/**
 * Correct-plaque celebration -- spec §4.
 *
 * Spec describes three OVERLAPPING phases:
 *
 *   phase 1   t=0.00 → 0.40 s   rotation 0   → -60°   easeInOut
 *   phase 2   t=0.05 → 0.65 s   rotation -60 → +60    easeInOut
 *   phase 3   t=0.55 → 0.95 s   rotation +60 → 0      easeInOut
 *
 * The legacy used iOS animateWithDuration:delay:, where each new
 * animation replaces any in-flight animation on the same layer
 * (last-writer-wins per property).  Replicating that on top of three
 * concurrent animateOver calls would need per-frame coordination we
 * don't otherwise have.
 *
 * Instead we drive a single animateOver with a piecewise rotation
 * function that preserves the spec's peaks and duration.
 *
 * IMPORTANT -- why each segment uses easeInOut and NOT linear lerp:
 *
 *   Each segment is lerp(start, end, easeInOut(local)) where
 *   easeInOut(u) = 0.5 · (1 - cos(π·u)).  The derivative
 *   d/du[easeInOut(u)] = 0.5·π·sin(π·u) is ZERO at both u=0 and u=1.
 *
 *   So at every segment boundary (-60° peak at t=0.20, +60° peak at
 *   t=0.60) the rotation velocity is zero on BOTH sides.  The plaque
 *   decelerates into each extreme, comes to rest momentarily, and
 *   accelerates from rest toward the next extreme.  Pendulum motion.
 *   No velocity sign flip, no "bouncing off a wall" robotic feel.
 *
 *   If this is ever changed to linear segments (lerp without easing)
 *   the velocity WILL flip sign instantly at each peak.  Don't do
 *   that.  See choice-plaque.test.js
 *   `animateChoicePlaqueCorrect > velocity is near-zero at segment
 *   boundaries` for a regression guard.
 *
 * Alternative visual: a damped sine envelope sweeps continuously
 * through the peaks (no pause at extremes), reading more as "smooth
 * bounce" than "pendulum swing."  The pendulum feel matches the
 * legacy iOS render exactly and gives the eye a beat to register the
 * left/right arcs in a kids-app reward moment, so we stick with it.
 * If a future design pass wants the sine sweep instead, replace the
 * piecewise function with e.g.
 *   deg = -60 * Math.exp(-0.7*t) * Math.sin(2*Math.PI*1.25*t + Math.PI);
 *
 * Scale locks to correctScale up front and stays there throughout.
 *
 * @param {*} plaque
 * @param {{ cfg: object, cancel?: { cancelled: boolean } }} opts
 */
export async function animateChoicePlaqueCorrect(plaque, opts) {
  const { cfg, cancel } = opts;
  const { correct } = quizPlaqueScales(cfg);
  setScale(plaque, correct);
  plaque.alpha = 1.0;

  const TOTAL_MS = 950;
  // Piecewise rotation envelope.  Boundaries at the spec's peak times.
  //   [0.00, 0.20] s :  0° → -60°  (easeInOut)
  //   [0.20, 0.60] s : -60 → +60   (easeInOut)
  //   [0.60, 0.95] s : +60 → 0     (easeInOut)
  await animateOver(TOTAL_MS, (t) => {
    const tSec = t * 0.95;
    let deg;
    if (tSec <= 0.20) {
      const local = tSec / 0.20;
      deg = lerp(0, -60, easeInOut(local));
    } else if (tSec <= 0.60) {
      const local = (tSec - 0.20) / 0.40;
      deg = lerp(-60, 60, easeInOut(local));
    } else {
      const local = (tSec - 0.60) / 0.35;
      deg = lerp(60, 0, easeInOut(local));
    }
    plaque.rotation = toRad(deg);
  }, cancel);
  if (cancel?.cancelled) return;

  plaque.rotation = 0;
}

/**
 * Incorrect-plaque dim -- spec §4.
 *
 *   0.000 → 0.400 s   scale → incorrectScale
 *                     alpha → 0.35
 *                     image → incorrectImage
 *                     easeIn
 *
 * Image-swap timing: INSTANTANEOUS at t=0.  Legacy iOS treats UIImage
 * as non-animatable inside animateWithDuration:, so the swap happens
 * synchronously at animation-block start while scale + alpha tween.
 * A crossfade would require two sprite layers, which the legacy app
 * does not do.  Swapping at t=0 rather than t=1 also means the visible
 * plaque matches the emitted "wrong-answer" notification the instant
 * the player sees any reaction.
 *
 * Starts from whatever scale / alpha the plaque is currently at, so
 * a wrong-tap that lands during the touch-in surge doesn't snap back
 * to displayScale before dimming.
 *
 * @param {*} plaque
 * @param {{ cfg: object, cancel?: { cancelled: boolean } }} opts
 */
export async function animateChoicePlaqueIncorrect(plaque, opts) {
  const { cfg, cancel } = opts;
  const { incorrect } = quizPlaqueScales(cfg);
  const fromScale = getScale(plaque);
  const fromAlpha = plaque.alpha ?? 1.0;

  // Instant texture swap (before the tween starts).
  if (plaque._incorrectTexture && plaque._plaqueSprite) {
    plaque._plaqueSprite.texture = plaque._incorrectTexture;
  }

  await animateOver(400, (t) => {
    const e = easeIn(t);
    setScale(plaque, lerp(fromScale, incorrect, e));
    plaque.alpha = lerp(fromAlpha, 0.35, e);
  }, cancel);
  if (cancel?.cancelled) return;

  setScale(plaque, incorrect);
  plaque.alpha = 0.35;
}
